"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  getConsentPreferences,
  hasConsentResponse,
  setConsentPreferences,
  DEFAULT_PREFERENCES,
  type ConsentPreferences,
} from "@/lib/legal/consent";
import { event } from "@/lib/gtag";

// ---------------------------------------------------------------------------
// CookieConsent banner
// ---------------------------------------------------------------------------
// Rendered once at the root layout. Hidden once the visitor has accepted,
// rejected, or customised their preferences. Re-opened via the
// "Cookie preferences" footer link (dispatches "open-cookie-prefs" event).

export default function CookieConsent() {
  const t = useTranslations("cookieConsent");
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [hasResponded, setHasResponded] = useState(false);
  const [prefs, setPrefs] = useState<Omit<ConsentPreferences, "necessary">>({
    analytics: DEFAULT_PREFERENCES.analytics,
  });

  useEffect(() => {
    const stored = getConsentPreferences();
    if (stored) {
      setPrefs({ analytics: stored.analytics });
      setHasResponded(true);
      return;
    }
    setVisible(true);
  }, []);

  const openPreferences = useCallback(() => {
    const stored = getConsentPreferences();
    setPrefs({
      analytics: stored?.analytics ?? DEFAULT_PREFERENCES.analytics,
    });
    setHasResponded(hasConsentResponse());
    setShowPrefs(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    const handler = () => openPreferences();
    window.addEventListener("open-cookie-prefs", handler);
    return () => window.removeEventListener("open-cookie-prefs", handler);
  }, [openPreferences]);

  const closePreferences = useCallback(() => {
    setShowPrefs(false);
    if (hasResponded) {
      setVisible(false);
    }
  }, [hasResponded]);

  const commitPreferences = useCallback(
    (next: Omit<ConsentPreferences, "necessary">, action: string) => {
      setConsentPreferences(next);
      setPrefs(next);
      setHasResponded(true);
      setShowPrefs(false);
      setVisible(false);
      event("consent_banner_action", {
        action,
        analytics: next.analytics,
      });
    },
    []
  );

  const handleAcceptAll = useCallback(
    () => commitPreferences({ analytics: true }, "accept_all"),
    [commitPreferences]
  );

  const handleRejectAll = useCallback(
    () => commitPreferences({ analytics: false }, "reject_all"),
    [commitPreferences]
  );

  const handleSavePrefs = useCallback(
    () => commitPreferences(prefs, "save_preferences"),
    [commitPreferences, prefs]
  );

  return (
    <>
      {visible ? (
        <div
          role="dialog"
          aria-labelledby={
            showPrefs ? "cookie-preferences-title" : "cookie-consent-title"
          }
          aria-modal="false"
          className="fixed inset-x-0 bottom-0 z-[9999] border-t border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur sm:px-6 sm:py-4"
        >
          <div className="mx-auto max-w-4xl">
            {!showPrefs ? (
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                <div className="space-y-1">
                  <p
                    id="cookie-consent-title"
                    className="text-sm font-semibold text-slate-900"
                  >
                    {t("title")}
                  </p>
                  <p className="max-w-3xl text-sm leading-6 text-slate-600">
                    {t("summary")}{" "}
                    <Link
                      href="/legal/cookies"
                      className="font-medium underline underline-offset-2 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      {t("policy")}
                    </Link>
                  </p>
                </div>
                <div className="grid shrink-0 grid-cols-3 gap-2 sm:flex">
                  <button
                    type="button"
                    onClick={() => setShowPrefs(true)}
                    className="min-h-11 rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    {t("manage")}
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="min-h-11 rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    {t("rejectAll")}
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="min-h-11 rounded bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    {t("acceptAll")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p
                  id="cookie-preferences-title"
                  className="text-sm font-semibold text-slate-900"
                >
                  {t("preferencesTitle")}
                </p>

                <label className="flex items-center gap-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="accent-blue-700"
                  />
                  <span>
                    <strong>{t("necessaryTitle")}</strong> - {t("necessaryDescription")}
                  </span>
                </label>

                <label className="flex items-center gap-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) =>
                      setPrefs({ analytics: e.target.checked })
                    }
                    className="accent-blue-700"
                  />
                  <span>
                    <strong>{t("analyticsTitle")}</strong> - {t("analyticsDescription")}
                  </span>
                </label>

                <p className="text-xs text-slate-500">
                  {t("reliability")}
                </p>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closePreferences}
                    className="min-h-11 rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    {hasResponded ? t("close") : t("back")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePrefs}
                    className="min-h-11 rounded bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    {t("save")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
